'use server';

import { createClient, createAdminClient } from '@/lib/supabase/server';
import { isStaffRole } from '@/lib/roles';
import {
  filterChangelog,
  filterHighlights,
  framingForView,
  releaseHasViewContent,
  type FilteredChangelog,
  type FilteredHighlight,
  type ReleaseView,
} from '@/lib/releases/audience';

export interface ReleaseHighlight {
  title: string;
  body: string;
  staffOnly?: boolean;
}

export interface ReleaseChangelog {
  new?: FilteredChangelogLine[];
  improved?: FilteredChangelogLine[];
  fixed?: FilteredChangelogLine[];
}

export type FilteredChangelogLine = {
  text: string;
  staffOnly?: boolean;
};

export interface AppRelease {
  version: string;
  bumpType: 'patch' | 'minor' | 'major';
  summary: string;
  releasedAt: string;
  headline: string | null;
  intro: string | null;
  highlights: ReleaseHighlight[];
  changelog: ReleaseChangelog;
}

function filterRow(r: Record<string, unknown>, view: ReleaseView): AppRelease {
  const highlights = filterHighlights(r.highlights, view);
  const changelog = filterChangelog(r.changelog, view);
  const framing = framingForView(r, view);
  return {
    version: String(r.version),
    bumpType: r.bump_type as AppRelease['bumpType'],
    summary: view === 'public' || view === 'student' ? '' : String(r.summary ?? ''),
    releasedAt: String(r.released_at),
    headline: framing.headline,
    intro: framing.intro,
    highlights,
    changelog,
  };
}

function toLines(section: FilteredChangelog['new']): FilteredChangelogLine[] {
  return section.map((l) => ({ text: l.text, staffOnly: l.staffOnly }));
}

function withLines(rel: AppRelease, changelog: FilteredChangelog, highlights: FilteredHighlight[]): AppRelease {
  return {
    ...rel,
    highlights: highlights.map((h) => ({ title: h.title, body: h.body, staffOnly: h.staffOnly })),
    changelog: {
      new: toLines(changelog.new),
      improved: toLines(changelog.improved),
      fixed: toLines(changelog.fixed),
    },
  };
}

async function viewerRole(): Promise<{ view: ReleaseView; userId: string | null }> {
  const supabase = await createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { view: 'public', userId: null };
  const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).maybeSingle();
  return { view: isStaffRole(profile?.role) ? 'staff' : 'student', userId: user.id };
}

async function loadReleaseRows() {
  const admin = await createAdminClient();
  return admin.from('app_releases').select('*').order('released_at', { ascending: false });
}

/** Latest minor/major this viewer may see. */
export async function getLatestNotifiableRelease(): Promise<AppRelease | null> {
  const { view } = await viewerRole();
  if (view === 'public') return null;
  const { data, error } = await loadReleaseRows();

  if (error) {
    console.error('[releases] getLatestNotifiableRelease:', error.message);
    return null;
  }
  for (const row of data ?? []) {
    if (row.bump_type === 'patch') continue;
    const highlights = filterHighlights(row.highlights, view);
    const changelog = filterChangelog(row.changelog, view);
    if (!releaseHasViewContent(highlights, changelog)) continue;
    const filtered = filterRow(row as Record<string, unknown>, view);
    return withLines(filtered, changelog, highlights);
  }
  return null;
}

export async function acknowledgeAppRelease(version: string): Promise<void> {
  const supabase = await createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  const admin = await createAdminClient();
  const { error } = await admin
    .from('profiles')
    .update({ last_seen_app_version: version })
    .eq('id', user.id);

  if (error) console.error('[releases] acknowledgeAppRelease:', error.message);
}

export async function getUnseenAppRelease(): Promise<AppRelease | null> {
  const { view, userId } = await viewerRole();
  if (view === 'public' || !userId) return null;

  const supabase = await createClient();
  const { data: profile } = await supabase
    .from('profiles')
    .select('last_seen_app_version')
    .eq('id', userId)
    .maybeSingle();
  const lastSeen = (profile as { last_seen_app_version?: string | null } | null)?.last_seen_app_version;

  const { data, error } = await loadReleaseRows();

  if (error) {
    console.error('[releases] getUnseenAppRelease:', error.message);
    return null;
  }

  for (const row of data ?? []) {
    if (row.bump_type === 'patch') continue;
    const highlights = filterHighlights(row.highlights, view);
    const changelog = filterChangelog(row.changelog, view);
    if (!releaseHasViewContent(highlights, changelog)) continue;
    if (lastSeen === row.version) return null;
    const filtered = filterRow(row as Record<string, unknown>, view);
    return withLines(filtered, changelog, highlights);
  }
  return null;
}

/** Changelog for this session: staff, student, or signed-out public. */
export async function getAllReleases(): Promise<AppRelease[]> {
  const { view } = await viewerRole();
  const { data, error } = await loadReleaseRows();

  if (error) {
    console.error('[releases] getAllReleases:', error.message);
    return [];
  }

  const out: AppRelease[] = [];
  for (const row of data ?? []) {
    const highlights = filterHighlights(row.highlights, view);
    const changelog = filterChangelog(row.changelog, view);
    const isPatch = row.bump_type === 'patch';
    if (!releaseHasViewContent(highlights, changelog)) {
      if (view === 'staff' && isPatch && row.summary) {
        out.push(
          withLines(filterRow(row as Record<string, unknown>, view), changelog, highlights),
        );
      }
      continue;
    }
    out.push(withLines(filterRow(row as Record<string, unknown>, view), changelog, highlights));
  }
  return out;
}
